import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { LogoMark } from "./Logo";
import { SearchDialog } from "./SearchDialog";
import { scrollToId } from "@/lib/scroll";

const links = [
  { id: "about", label: "About", href: "/#about" },
  { id: "topics", label: "Topics", href: "/topics" },
  { id: "connect", label: "Connect", href: "#connect" },
  { id: "people", label: "People", href: "/people" },
];

export const Nav = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === "/" || pathname === "/home";
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const go = (id) => {
    if (id === "people") return navigate("/people");
    if (id === "topics" && !isHome) return navigate("/topics");
    if (id === "connect" || isHome) return scrollToId(id);
    return navigate(`/#${id}`);
  };
  return (
    <>
    <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.6 }}
      className="fixed top-4 inset-x-4 md:inset-x-8 z-50 flex items-center justify-between rounded-full border border-white/10 bg-ink/60 backdrop-blur-xl px-4 md:px-6 py-3"
    >
      <Link data-testid="nav-logo" to="/" onClick={() => isHome && scrollToId("top")} className="flex items-center gap-2 text-lime group">
        <LogoMark className="w-7 h-7 transition-transform duration-700 group-hover:rotate-[72deg]" />
        <span className="hidden sm:inline font-display font-bold text-sm tracking-wide text-white">DESI<span className="text-lime">/</span>SCIENCE<span className="text-lime">/</span>PAGE</span>
      </Link>
      <nav className="flex items-center gap-1">
        {links.map((l) => (
          <a
            key={l.id}
            href={l.href}
            data-testid={`nav-link-${l.id}`}
            onClick={(e) => { e.preventDefault(); go(l.id); }}
            className={`font-mono text-[11px] sm:text-[12px] uppercase tracking-[0.06em] md:tracking-[0.2em] hover:text-ink hover:bg-lime rounded-full px-1.5 sm:px-2 md:px-4 py-1.5 transition-colors duration-300 ${pathname === l.href ? "bg-lime text-ink" : "text-zinc-300"}`}
          >
            {l.label}
          </a>
        ))}
        <button
          data-testid="nav-search-btn"
          onClick={() => setSearchOpen(true)}
          aria-label="Search topics"
          className="ml-1 flex items-center gap-2 rounded-full border border-white/15 text-zinc-200 hover:text-ink hover:bg-lime hover:border-lime px-2.5 md:px-3 py-1.5 transition-colors duration-300"
        >
          <Search className="w-4 h-4" />
          <span className="hidden lg:inline font-mono text-[12px] text-zinc-400">⌘K</span>
        </button>
      </nav>
    </motion.header>
    </>
  );
};
